﻿/*
 * -------------------------------------------------------------------------------------------------------------------
 * Copyright (c) 2025 Christian Bannard
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * 
 *     http://www.apache.org/licenses/LICENSE-2.0
 * 
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * -------------------------------------------------------------------------------------------------------------------
 * File: c:\localdev\templaredge\frontend\src\pages\Tasks\TaskView.tsx
 *  
 * Author: Christian Bannard
 * Created: 2025-11-23
 *  
 * Description: Task view modal with history, notes, and attachments
 * -------------------------------------------------------------------------------------------------------------------
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	-------------------------------------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Task, TaskNote, TaskAttachment, TaskHistoryEntry } from '../../types/task';
import {
	FiX,
	FiEdit,
	FiUser,
	FiCalendar,
	FiClock,
	FiPaperclip,
	FiFile,
	FiDownload,
	FiTrash2,
	FiMessageSquare,
	FiSend,
	FiAlertCircle
} from 'react-icons/fi';

interface TaskViewProps {
	taskId: string;
	onClose: () => void;
}

const TaskView: React.FC<TaskViewProps> = ({ taskId, onClose }) => {
	const { token, user } = useAuth();
	const navigate = useNavigate();

	const [task, setTask] = useState<Task | null>(null);
	const [notes, setNotes] = useState<TaskNote[]>([]);
	const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
	const [history, setHistory] = useState<TaskHistoryEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Note creation
	const [newNoteContent, setNewNoteContent] = useState('');
	const [addingNote, setAddingNote] = useState(false);

	// Active tab
	const [activeTab, setActiveTab] = useState<'notes' | 'attachments' | 'history'>('notes');

	useEffect(() => {
		if (token && taskId) {
			loadTaskDetails();
		}
	}, [token, taskId]);

	const loadTaskDetails = async () => {
		if (!token) return;

		setLoading(true);
		setError(null);

		try {
			// Load task
			const taskResponse = await apiCall(
				`${ENDPOINTS.TASKS.DETAIL}${taskId}/`,
				{ method: 'GET' },
				token
			);

			if (!taskResponse.ok) {
				throw new Error('Failed to load task');
			}

			const taskData: Task = await taskResponse.json();
			setTask(taskData);

			// Load notes
			const notesResponse = await apiCall(
				`${ENDPOINTS.TASKS.NOTES}${taskId}/notes/`,
				{ method: 'GET' },
				token
			);

			if (notesResponse.ok) {
				const notesData = await notesResponse.json();
				setNotes(notesData.results || notesData || []);
			}

			// Load attachments
			const attachmentsResponse = await apiCall(
				`${ENDPOINTS.TASKS.ATTACHMENTS}${taskId}/attachments/`,
				{ method: 'GET' },
				token
			);

			if (attachmentsResponse.ok) {
				const attachmentsData = await attachmentsResponse.json();
				setAttachments(attachmentsData.results || attachmentsData || []);
			}

			// Load history
			const historyResponse = await apiCall(
				`${ENDPOINTS.TASKS.HISTORY}${taskId}/history/`,
				{ method: 'GET' },
				token
			);

			if (historyResponse.ok) {
				const historyData = await historyResponse.json();
				setHistory(historyData.results || historyData || []);
			}
		} catch (err) {
			console.error('Error loading task details:', err);
			setError('Failed to load task details');
		} finally {
			setLoading(false);
		}
	};

	const handleAddNote = async () => {
		if (!token || !newNoteContent.trim()) return;

		setAddingNote(true);

		try {
			const response = await apiCall(
				`${ENDPOINTS.TASKS.NOTES}${taskId}/notes/`,
				{
					method: 'POST',
					body: JSON.stringify({ content: newNoteContent.trim() }),
				},
				token
			);

			if (!response.ok) {
				throw new Error('Failed to add note');
			}

			const newNote: TaskNote = await response.json();
			setNotes([newNote, ...notes]);
			setNewNoteContent('');

			// Reload task to update counts
			loadTaskDetails();
		} catch (err) {
			console.error('Error adding note:', err);
			alert('Failed to add note');
		} finally {
			setAddingNote(false);
		}
	};

	const handleDeleteAttachment = async (attachmentId: string) => {
		if (!token) return;

		if (!window.confirm('Are you sure you want to delete this attachment?')) {
			return;
		}

		try {
			const response = await apiCall(
				`${ENDPOINTS.TASKS.ATTACHMENTS}${taskId}/attachments/${attachmentId}/`,
				{ method: 'DELETE' },
				token
			);

			if (!response.ok) {
				throw new Error('Failed to delete attachment');
			}

			setAttachments(attachments.filter(att => att.id !== attachmentId));
			
			// Reload task to update counts
			loadTaskDetails();
		} catch (err) {
			console.error('Error deleting attachment:', err);
			alert('Failed to delete attachment');
		}
	};

	const handleEdit = () => {
		navigate(`/tasks/edit/${taskId}`);
		onClose();
	};

	const handleStatusChange = async (newStatus: Task['status']) => {
		if (!token || !task) return;

		try {
			const response = await apiCall(
				`${ENDPOINTS.TASKS.UPDATE}${taskId}/`,
				{
					method: 'PATCH',
					body: JSON.stringify({ status: newStatus }),
				},
				token
			);

			if (!response.ok) {
				throw new Error('Failed to update status');
			}

			const updatedTask: Task = await response.json();
			setTask(updatedTask);
			
			// Reload to get updated history
			loadTaskDetails();
		} catch (err) {
			console.error('Error updating status:', err);
			alert('Failed to update status');
		}
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-AU', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
	};

	const getPriorityColor = (priority: string) => {
		switch (priority) {
			case 'urgent': return 'badge-error';
			case 'high': return 'badge-warning';
			case 'medium': return 'badge-info';
			case 'low': return 'badge-ghost';
			default: return 'badge-ghost';
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'completed': return 'badge-success';
			case 'in_progress': return 'badge-primary';
			case 'open': return 'badge-info';
			case 'cancelled': return 'badge-ghost';
			default: return 'badge-ghost';
		}
	};

	const isOverdue = task?.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

	if (loading) {
		return (
			<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
				<div className="bg-base-100 rounded-lg p-8">
					<span className="loading loading-spinner loading-lg"></span>
				</div>
			</div>
		);
	}

	if (error || !task) {
		return (
			<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
				<div className="bg-base-100 rounded-lg p-8 max-w-md">
					<div className="alert alert-error">
						<FiAlertCircle className="w-5 h-5" />
						<span>{error || 'Task not found'}</span>
					</div>
					<div className="flex justify-end mt-4">
						<button onClick={onClose} className="btn btn-ghost">Close</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
			<div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
				{/* Header */}
				<div className="flex justify-between items-start p-6 border-b border-base-300">
					<div className="flex-1 pr-4">
						<h2 className="text-2xl font-bold mb-2">{task.title}</h2>
						<div className="flex flex-wrap gap-2 mb-3">
							<span className={`badge ${getPriorityColor(task.priority)}`}>
								{task.priority}
							</span>
							<span className={`badge ${getStatusColor(task.status)}`}>
								{task.status.replace('_', ' ')}
							</span>
							{isOverdue && (
								<span className="badge badge-error gap-1">
									<FiAlertCircle className="w-3 h-3" />
									Overdue
								</span>
							)}
						</div>
						{task.description && (
							<p className="text-sm opacity-80 mb-3">{task.description}</p>
						)}
						<div className="flex flex-wrap gap-4 text-sm opacity-70">
							{task.patient_name && (
								<div className="flex items-center gap-1">
									<FiUser className="w-4 h-4" />
									<span>{task.patient_name}</span>
								</div>
							)}
							{task.assigned_to_name && (
								<div className="flex items-center gap-1">
									<FiUser className="w-4 h-4" />
									<span>Assigned to: {task.assigned_to_name}</span>
								</div>
							)}
							{task.due_date && (
								<div className={`flex items-center gap-1 ${isOverdue ? 'text-error font-semibold' : ''}`}>
									<FiCalendar className="w-4 h-4" />
									<span>Due: {formatDate(task.due_date)}</span>
								</div>
							)}
							<div className="flex items-center gap-1">
								<FiClock className="w-4 h-4" />
								<span>Created: {formatDate(task.created_at)}</span>
							</div>
						</div>
					</div>
					<div className="flex gap-2">
						<button onClick={handleEdit} className="btn btn-ghost btn-sm gap-2">
							<FiEdit className="w-4 h-4" />
							Edit
						</button>
						<button onClick={onClose} className="btn btn-ghost btn-sm btn-square">
							<FiX className="w-5 h-5" />
						</button>
					</div>
				</div>

				{/* Quick Status Actions */}
				{task.status !== 'completed' && task.status !== 'cancelled' && (
					<div className="px-6 py-3 bg-base-200 border-b border-base-300">
						<div className="flex gap-2">
							<span className="text-sm font-semibold mr-2">Quick Actions:</span>
							{task.status === 'open' && (
								<button
									onClick={() => handleStatusChange('in_progress')}
									className="btn btn-primary btn-xs"
								>
									Start Task
								</button>
							)}
							{task.status === 'in_progress' && (
								<button
									onClick={() => handleStatusChange('completed')}
									className="btn btn-success btn-xs"
								>
									Complete Task
								</button>
							)}
							<button
								onClick={() => handleStatusChange('cancelled')}
								className="btn btn-ghost btn-xs"
							>
								Cancel Task
							</button>
						</div>
					</div>
				)}

				{/* Tabs */}
				<div className="flex border-b border-base-300 px-6">
					<button
						onClick={() => setActiveTab('notes')}
						className={`px-4 py-3 font-medium transition-colors ${
							activeTab === 'notes'
								? 'border-b-2 border-primary text-primary'
								: 'text-base-content opacity-60 hover:opacity-100'
						}`}
					>
						<div className="flex items-center gap-2">
							<FiMessageSquare className="w-4 h-4" />
							<span>Notes ({notes.length})</span>
						</div>
					</button>
					<button
						onClick={() => setActiveTab('attachments')}
						className={`px-4 py-3 font-medium transition-colors ${
							activeTab === 'attachments'
								? 'border-b-2 border-primary text-primary'
								: 'text-base-content opacity-60 hover:opacity-100'
						}`}
					>
						<div className="flex items-center gap-2">
							<FiPaperclip className="w-4 h-4" />
							<span>Attachments ({attachments.length})</span>
						</div>
					</button>
					<button
						onClick={() => setActiveTab('history')}
						className={`px-4 py-3 font-medium transition-colors ${
							activeTab === 'history'
								? 'border-b-2 border-primary text-primary'
								: 'text-base-content opacity-60 hover:opacity-100'
						}`}
					>
						<div className="flex items-center gap-2">
							<FiClock className="w-4 h-4" />
							<span>History ({history.length})</span>
						</div>
					</button>
				</div>

				{/* Tab Content */}
				<div className="flex-1 overflow-y-auto p-6">
					{/* Notes Tab */}
					{activeTab === 'notes' && (
						<div className="space-y-4">
							{/* Add Note */}
							<div className="card bg-base-200">
								<div className="card-body p-4">
									<textarea
										value={newNoteContent}
										onChange={(e) => setNewNoteContent(e.target.value)}
										className="textarea textarea-bordered w-full h-24"
										placeholder="Add a note..."
									/>
									<div className="flex justify-end mt-2">
										<button
											onClick={handleAddNote}
											disabled={!newNoteContent.trim() || addingNote}
											className="btn btn-primary btn-sm gap-2"
										>
											<FiSend className="w-4 h-4" />
											{addingNote ? 'Adding...' : 'Add Note'}
										</button>
									</div>
								</div>
							</div>

							{/* Notes List */}
							{notes.length === 0 ? (
								<div className="text-center py-8 opacity-60">
									<FiMessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
									<p>No notes yet</p>
								</div>
							) : (
								<div className="space-y-3">
									{notes.map((note) => (
										<div key={note.id} className="card bg-base-100 border border-base-300">
											<div className="card-body p-4">
												<div className="flex justify-between items-start mb-2">
													<div className="flex items-center gap-2">
														<FiUser className="w-4 h-4 opacity-50" />
														<span className="font-semibold text-sm">
															{note.user_name || 'Unknown User'}
														</span>
													</div>
													<span className="text-xs opacity-60">
														{formatDate(note.created_at)}
													</span>
												</div>
												<p className="text-sm whitespace-pre-wrap">{note.content}</p>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{/* Attachments Tab */}
					{activeTab === 'attachments' && (
						<div>
							{attachments.length === 0 ? (
								<div className="text-center py-8 opacity-60">
									<FiPaperclip className="w-12 h-12 mx-auto mb-2 opacity-30" />
									<p>No attachments</p>
								</div>
							) : (
								<div className="space-y-2">
									{attachments.map((attachment) => (
										<div
											key={attachment.id}
											className="flex items-center justify-between p-4 bg-base-100 border border-base-300 rounded-lg"
										>
											<div className="flex items-center gap-3">
												<FiFile className="w-6 h-6 opacity-50" />
												<div>
													<div className="font-medium">{attachment.file_name}</div>
													<div className="text-xs opacity-60 flex items-center gap-2">
														<span>{formatFileSize(attachment.file_size)}</span>
														{attachment.is_from_patient_file && (
															<span className="badge badge-info badge-xs">
																From Patient File
															</span>
														)}
														<span>
															Uploaded by {attachment.uploaded_by_name || 'Unknown'} on{' '}
															{formatDate(attachment.uploaded_at)}
														</span>
													</div>
												</div>
											</div>
											<div className="flex gap-2">
												{attachment.file_url && (
													<a
														href={attachment.file_url}
														target="_blank"
														rel="noopener noreferrer"
														className="btn btn-ghost btn-sm btn-square"
														title="Open in new window"
													>
														<FiDownload className="w-4 h-4" />
													</a>
												)}
												<button
													onClick={() => handleDeleteAttachment(attachment.id)}
													className="btn btn-ghost btn-sm btn-square text-error"
													title="Delete"
												>
													<FiTrash2 className="w-4 h-4" />
												</button>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{/* History Tab */}
					{activeTab === 'history' && (
						<div>
							{history.length === 0 ? (
								<div className="text-center py-8 opacity-60">
									<FiClock className="w-12 h-12 mx-auto mb-2 opacity-30" />
									<p>No history yet</p>
								</div>
							) : (
								<div className="space-y-4">
									{history.map((entry) => (
										<div key={entry.id} className="flex gap-4">
											<div className="flex flex-col items-center">
												<div className="w-3 h-3 rounded-full bg-primary"></div>
												<div className="w-0.5 flex-1 bg-base-300 mt-2"></div>
											</div>
											<div className="flex-1 pb-4">
												<div className="flex items-center gap-2 mb-1">
													<span className="font-semibold text-sm">
														{entry.user_name || 'System'}
													</span>
													<span className="text-xs opacity-60">
														{formatDate(entry.created_at)}
													</span>
												</div>
												<p className="text-sm opacity-80">{entry.description}</p>
												{entry.old_value && entry.new_value && (
													<div className="text-xs opacity-60 mt-1">
														Changed from <span className="font-mono">{entry.old_value}</span> to{' '}
														<span className="font-mono">{entry.new_value}</span>
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default TaskView;
